import { executeGraphQL } from '../../../src/graphql/executeGraphQL';
import type { GraphQLRequestOverrides, GraphqlDocumentOperation } from '../../../src/graphql/executeGraphQL';
import type { GqlGetWorkbookQuery, GqlGetWorkbookQueryVariables } from './GetWorkbook';
import type { GqlGetWorkbookWithApprovalFlowQuery, GqlGetWorkbookWithApprovalFlowQueryVariables } from './GetWorkbookWithApprovalFlow';
import type { GqlLockAndVersionWorkbookMutation, GqlLockAndVersionWorkbookMutationVariables } from './LockAndVersionWorkbook';

const GetWorkbookOperation: GraphqlDocumentOperation = {
  name: 'GetWorkbook',
  type: 'query',
  document: `query GetWorkbook($workbookId: ID!, $worksheetId: ID!, $workstepId: ID, $skipWorksheets: Boolean!) {
  workbook(id: $workbookId) {
    ...WorkbookCore
  }
}

fragment WorkbookCore on WorkbookGraphQL {
  serverErrors {
    field
    message
    path
  }
  id
  name
  description
  type
  createdAt
  updatedAt
  isArchived
  effectivePermissions {
    read
    write
    manage
  }
  owner {
    id
    name
    email
    username
    type
  }
  parentFolderId
  ancestors {
    id
    name
    type
    translationKey
  }
  isLocked
  lockedMetadata
  workbookState
  evidenceTableDefinitionId
  worksheets @skip(if: $skipWorksheets) {
    id
    name
    createdAt
    updatedAt
    isArchived
    isDashboard
    duplicationErrorDocument
    report {
      id
      name
      type
    }
    boardId
    currentWorkstep {
      id
    }
  }
  selectedWorksheet: worksheet(id: $worksheetId) {
    id
    name
    serverErrors {
      field
      message
      path
    }
    isDashboard
    boardId
    isArchived
    createdAt
    updatedAt
    duplicationErrorDocument
    report {
      id
      name
      type
    }
    currentWorkstep {
      id
      name
      type
      createdAt
      data
      previous
      next
      last
    }
    requestedWorkstep: workstep(id: $workstepId) {
      id
      name
      type
      createdAt
      data
      previous
      next
      last
    }
  }
}`,
};

const GetWorkbookWithApprovalFlowOperation: GraphqlDocumentOperation = {
  name: 'GetWorkbookWithApprovalFlow',
  type: 'query',
  document: `query GetWorkbookWithApprovalFlow($workbookId: ID!, $worksheetId: ID!, $workstepId: ID, $skipWorksheets: Boolean!) {
  workbook(id: $workbookId) {
    ...WorkbookCore
    ...ApprovalFlowSection
  }
}

fragment ApprovalFlowSection on WorkbookGraphQL {
  attachments {
    id
    itemId
    approvalFlowId
    assignedBy
    createdAt
    archived
  }
  approvalFlows {
    id
    name
    archived
    createdAt
    updatedAt
    steps {
      id
      approvalFlowId
      name
      lockRequired
      position
      archived
      status
      createdAt
      updatedAt
      permissions {
        read
        write
        manage
      }
    }
  }
  approvalFlowAssignments {
    id
    approvalFlowId
    stepId
    itemId
    approvedBy
    approvedAt
  }
}

fragment WorkbookCore on WorkbookGraphQL {
  serverErrors {
    field
    message
    path
  }
  id
  name
  description
  type
  createdAt
  updatedAt
  isArchived
  effectivePermissions {
    read
    write
    manage
  }
  owner {
    id
    name
    email
    username
    type
  }
  parentFolderId
  ancestors {
    id
    name
    type
    translationKey
  }
  isLocked
  lockedMetadata
  workbookState
  evidenceTableDefinitionId
  worksheets @skip(if: $skipWorksheets) {
    id
    name
    createdAt
    updatedAt
    isArchived
    isDashboard
    duplicationErrorDocument
    report {
      id
      name
      type
    }
    boardId
    currentWorkstep {
      id
    }
  }
  selectedWorksheet: worksheet(id: $worksheetId) {
    id
    name
    serverErrors {
      field
      message
      path
    }
    isDashboard
    boardId
    isArchived
    createdAt
    updatedAt
    duplicationErrorDocument
    report {
      id
      name
      type
    }
    currentWorkstep {
      id
      name
      type
      createdAt
      data
      previous
      next
      last
    }
    requestedWorkstep: workstep(id: $workstepId) {
      id
      name
      type
      createdAt
      data
      previous
      next
      last
    }
  }
}`,
};

const LockAndVersionWorkbookOperation: GraphqlDocumentOperation = {
  name: 'LockAndVersionWorkbook',
  type: 'mutation',
  document: `mutation LockAndVersionWorkbook($input: LockAndVersionWorkbookInput!) {
  lockAndVersionWorkbook(input: $input) {
    versionHistoryId
    versionNumber
    name
    lockErrors {
      id
      item {
        id
        name
        type
      }
      itemPath {
        id
        item {
          id
          name
          type
        }
        childRelationship
      }
    }
  }
}`,
};

export class sqWorkbookGqlApiClass {
  /** GraphQL query GetWorkbook (src/graphql/operations/workbook/GetWorkbook.graphql) */
  public getWorkbook(
    variables: GqlGetWorkbookQueryVariables,
    overrides?: GraphQLRequestOverrides,
  ): Promise<GqlGetWorkbookQuery> {
    return executeGraphQL(GetWorkbookOperation, variables, overrides);
  }

  /** GraphQL query GetWorkbookWithApprovalFlow (src/graphql/operations/workbook/GetWorkbookWithApprovalFlow.graphql) */
  public getWorkbookWithApprovalFlow(
    variables: GqlGetWorkbookWithApprovalFlowQueryVariables,
    overrides?: GraphQLRequestOverrides,
  ): Promise<GqlGetWorkbookWithApprovalFlowQuery> {
    return executeGraphQL(GetWorkbookWithApprovalFlowOperation, variables, overrides);
  }

  /** GraphQL mutation LockAndVersionWorkbook (src/graphql/operations/workbook/LockAndVersionWorkbook.graphql) */
  public lockAndVersionWorkbook(
    variables: GqlLockAndVersionWorkbookMutationVariables,
    overrides?: GraphQLRequestOverrides,
  ): Promise<GqlLockAndVersionWorkbookMutation> {
    return executeGraphQL(LockAndVersionWorkbookOperation, variables, overrides);
  }
}

export const sqWorkbookGqlApi = new sqWorkbookGqlApiClass();
